import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {ShieldCheck, Search, RefreshCw, Power, PowerOff, MapPin, Calendar, Mail} from 'lucide-react'
import SuperadminLayout from "../components/SuperadminLayout"

interface AdminProfile {
  _id: string
  userId: string
  email: string
  fullName?: string
  role: string
  region?: string
  accountStatus?: string
  createdAt?: string
  lastLoginAt?: string
  phoneNumber?: string
}

export default function SuperadminAdminsDirectory() {
  const [admins, setAdmins] = useState<AdminProfile[]>([])
  const [filteredAdmins, setFilteredAdmins] = useState<AdminProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("")

  useEffect(() => {
    fetchAdmins()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [admins, searchQuery, statusFilter])

  const fetchAdmins = async () => {
    try {
      setLoading(true)
      console.log("[Admins] Fetching admin profiles...")
      
      const result = await lumi.entities.UserProfiles.list({
        filter: { role: "admin" },
        sort: { _id: -1 }
      })

      console.log("[Admins] Fetched", result.list.length, "admins")
      setAdmins(result.list)
    } catch (error) {
      console.error("[Admins] Error fetching admins:", error)
      toast.error("Failed to load admins")
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...admins]

    // Search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(admin =>
        admin.email?.toLowerCase().includes(query) ||
        admin.fullName?.toLowerCase().includes(query) ||
        admin.userId?.toLowerCase().includes(query)
      )
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(admin => 
        (admin.accountStatus || "active") === statusFilter
      )
    }

    setFilteredAdmins(filtered)
  }

  const handleToggleStatus = async (admin: AdminProfile) => {
    const currentStatus = admin.accountStatus || "active"
    const newStatus = currentStatus === "active" ? "inactive" : "active"
    const action = newStatus === "active" ? "reactivate" : "deactivate"

    if (!confirm(`Are you sure you want to ${action} ${admin.fullName || admin.email}?`)) {
      return
    }

    try {
      await lumi.entities.UserProfiles.update(admin._id, {
        accountStatus: newStatus
      })

      // Create audit log
      await lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        actor_email: lumi.auth.user?.email || "",
        action: `admin_${action}`,
        target_id: admin.userId,
        target_email: admin.email,
        metadata: { 
          previous_status: currentStatus,
          new_status: newStatus 
        },
        created_at: new Date().toISOString()
      })

      toast.success(`Admin ${action}d successfully`)
      fetchAdmins()
    } catch (error) {
      console.error(`Error ${action}ing admin:`, error)
      toast.error(`Failed to ${action} admin`)
    }
  }

  return (
    <SuperadminLayout activeMenuItem="admins">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admins</h1>
              <p className="text-sm text-gray-600">
                {filteredAdmins.length} admin accounts
              </p>
            </div>
          </div>
          <button
            onClick={fetchAdmins}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Admins List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading admins...</p>
          </div>
        ) : filteredAdmins.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No admins found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Admin</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Region</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Last Login</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAdmins.map(admin => (
                    <tr key={admin._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white font-semibold">
                            {(admin.fullName || admin.email).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{admin.fullName || "—"}</p>
                            <p className="text-sm text-gray-500">{admin.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {admin.region ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {admin.region}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          (admin.accountStatus || "active") === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {(admin.accountStatus || "active").toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleDateString() : "Never"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleToggleStatus(admin)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                            (admin.accountStatus || "active") === "active"
                              ? "bg-red-100 text-red-700 hover:bg-red-200"
                              : "bg-green-100 text-green-700 hover:bg-green-200"
                          }`}
                        >
                          {(admin.accountStatus || "active") === "active" ? (
                            <>
                              <PowerOff className="w-3 h-3 inline mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <Power className="w-3 h-3 inline mr-1" />
                              Reactivate
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {filteredAdmins.map(admin => (
                <div key={admin._id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white font-semibold">
                      {(admin.fullName || admin.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{admin.fullName || "—"}</p>
                      <p className="text-sm text-gray-500">{admin.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (admin.accountStatus || "active") === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {(admin.accountStatus || "active").toUpperCase()}
                        </span>
                        {admin.region && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {admin.region}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Joined: {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : "—"}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Last: {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleDateString() : "Never"}
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleStatus(admin)}
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      (admin.accountStatus || "active") === "active"
                        ? "bg-red-100 text-red-700 hover:bg-red-200"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }`}
                  >
                    {(admin.accountStatus || "active") === "active" ? (
                      <>
                        <PowerOff className="w-4 h-4 inline mr-1" />
                        Deactivate Account
                      </>
                    ) : (
                      <>
                        <Power className="w-4 h-4 inline mr-1" />
                        Reactivate Account
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Info Notice */}
        <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-800">
            <strong>Note:</strong> This is a read-only directory with basic account management. 
            Admins have limited permissions (view/manage nurses, access requests, invites, messages). 
            They cannot access superadmin features or modify other admin accounts.
          </p>
        </div>
      </div>
    </SuperadminLayout>
  )
}
